import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { templateKeys } from '../template-query-keys';
import {
  createAutomationCategory,
  deleteAutomationCategory,
  getAutomationCategoryUsage,
  getTemplateCategories,
  listAutomationCategories,
  setTemplateCategories,
  updateAutomationCategory,
} from './api';

export const automationCategoryKeys = {
  all: ['automationCategories'] as const,
  list: () => [...automationCategoryKeys.all, 'list'] as const,
  usage: (categoryId: string) => [...automationCategoryKeys.all, 'usage', categoryId] as const,
  template: (templateId: string) => [...automationCategoryKeys.all, 'template', templateId] as const,
};

const STANDARD_CACHE = { staleTime: 5 * 60 * 1000, gcTime: 10 * 60 * 1000 };

// The templates list embeds each template's categories, so category changes refresh it too.
const invalidateCategoriesAndTemplates = (queryClient: QueryClient) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: automationCategoryKeys.all }),
    queryClient.invalidateQueries({ queryKey: templateKeys.lists() }),
  ]);

export const useAutomationCategories = () =>
  useQuery({
    queryKey: automationCategoryKeys.list(),
    queryFn: listAutomationCategories,
    ...STANDARD_CACHE,
  });

export const useTemplateCategories = (templateId: string) =>
  useQuery({
    queryKey: automationCategoryKeys.template(templateId),
    queryFn: () => getTemplateCategories(templateId),
    ...STANDARD_CACHE,
  });

export const useAutomationCategoryUsage = (categoryId: string) =>
  useQuery({
    queryKey: automationCategoryKeys.usage(categoryId),
    queryFn: () => getAutomationCategoryUsage(categoryId),
  });

export const useCreateAutomationCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createAutomationCategory,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: automationCategoryKeys.list() }),
  });
};

export const useUpdateAutomationCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateAutomationCategory,
    onSuccess: () => invalidateCategoriesAndTemplates(queryClient),
  });
};

export const useDeleteAutomationCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAutomationCategory,
    onSuccess: () => invalidateCategoriesAndTemplates(queryClient),
  });
};

export const useSetTemplateCategories = (templateId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setTemplateCategories,
    scope: { id: `template-categories:${templateId}` },
    onSuccess: () => invalidateCategoriesAndTemplates(queryClient),
  });
};
